import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, NgClass } from '@angular/common';
import { finalize } from 'rxjs';
import { UIChart } from 'primeng/chart';
import { Card } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { DashboardService } from '../services/dashboard.service';
import {
  DashboardConversation,
  DashboardOperator,
  DashboardResponse,
} from '../contracts/dashboard.interface';

// ─── Display types ────────────────────────────────────────────────────────────

interface KpiItem {
  label: string;
  value: string;
  icon: string;
  trendText: string;
  trendGood: boolean | null;
  sub: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BAR_COLORS = [
  'rgba(16,185,129,0.75)',
  'rgba(59,130,246,0.75)',
  'rgba(245,158,11,0.75)',
  'rgba(139,92,246,0.75)',
  'rgba(236,72,153,0.75)',
  'rgba(20,184,166,0.75)',
];

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [UIChart, Card, TableModule, Tag, NgClass, Tooltip, DatePipe],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  private destroyRef = inject(DestroyRef);

  weekOffset = 0;
  selectedOperatorIds: number[] = [];

  isLoading = false;
  error: string | null = null;

  allOperators: DashboardOperator[] = [];
  displayOperators: DashboardOperator[] = [];
  recentConversations: DashboardConversation[] = [];

  kpis: KpiItem[] = [];
  trendSubtitle = '';

  trendData: any;
  statusDoughnutData: any;
  operatorBarData: any;
  responseTimeData: any;
  lineChartOptions: any;
  doughnutChartOptions: any;
  barChartOptions: any;

  get isNewestWeek(): boolean { return this.weekOffset === 0; }
  get isOldestWeek(): boolean { return this.weekOffset <= -12; }

  get currentWeekLabel(): string {
    if (this.weekOffset === 0) return 'Corrente';
    if (this.weekOffset === -1) return 'Scorsa';
    return `${Math.abs(this.weekOffset)} sett. fa`;
  }

  get currentWeekRange(): string {
    const { dateFrom, dateTo } = this.getWeekDates();
    return this.formatWeekRange(dateFrom, dateTo);
  }

  get hasFilters(): boolean {
    return this.weekOffset !== 0 || this.selectedOperatorIds.length > 0;
  }

  get onlineCount(): number {
    return this.displayOperators.filter(op => op.status === 'online').length;
  }

  get offlineCount(): number {
    return this.displayOperators.filter(op => op.status === 'offline').length;
  }

  ngOnInit() {
    this.initChartOptions();
    this.fetchDashboard();
  }

  goToPrevWeek() {
    if (!this.isOldestWeek) { this.weekOffset--; this.fetchDashboard(); }
  }

  goToNextWeek() {
    if (!this.isNewestWeek) { this.weekOffset++; this.fetchDashboard(); }
  }

  toggleOperator(op: DashboardOperator) {
    const idx = this.selectedOperatorIds.indexOf(op.id);
    this.selectedOperatorIds = idx >= 0
      ? this.selectedOperatorIds.filter(id => id !== op.id)
      : [...this.selectedOperatorIds, op.id];
    this.fetchDashboard();
  }

  clearFilters() {
    this.weekOffset = 0;
    this.selectedOperatorIds = [];
    this.fetchDashboard();
  }

  isOperatorSelected(id: number): boolean {
    return this.selectedOperatorIds.includes(id);
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────

  private fetchDashboard() {
    const { dateFrom, dateTo } = this.getWeekDates();
    const operatorIds = this.selectedOperatorIds.length > 0 ? this.selectedOperatorIds : undefined;

    this.isLoading = true;
    this.error = null;

    this.dashboardService.getDashboard(dateFrom, dateTo, operatorIds)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading = false),
      )
      .subscribe({
        next: (data) => this.applyResponse(data),
        error: () => { this.error = 'Impossibile caricare i dati del dashboard. Riprova più tardi.'; },
      });
  }

  private applyResponse(data: DashboardResponse) {
    if (this.selectedOperatorIds.length === 0) {
      this.allOperators = data.operators;
    }
    this.displayOperators = data.operators;
    this.recentConversations = data.recentConversations;

    this.kpis = this.buildKpis(data);
    this.trendSubtitle = data.period.prevLabel
      ? `${data.period.label} vs ${data.period.prevLabel}`
      : data.period.label;

    this.buildTrendChart(data);
    this.buildStatusChart(data);
    this.buildOperatorCharts(data);
  }

  // ─── KPIs ─────────────────────────────────────────────────────────────────

  private buildKpis(data: DashboardResponse): KpiItem[] {
    const { kpis, period } = data;
    const isFiltered = this.selectedOperatorIds.length > 0;

    const totalTrend = this.pctTrend(kpis.totalConversations, kpis.totalConversationsPrev);
    const respDiff = kpis.avgResponseTimeMin - kpis.avgResponseTimePrevMin;
    const slaTrend = this.pctTrend(kpis.slaPercent, kpis.slaPercentPrev);

    return [
      {
        label: 'Conversazioni Totali',
        value: kpis.totalConversations.toString(),
        icon: 'pi pi-comments',
        trendText: totalTrend?.text ?? '',
        trendGood: totalTrend?.good ?? null,
        sub: isFiltered ? 'operatori selezionati' : period.label,
      },
      {
        label: 'Conversazioni Aperte',
        value: kpis.openConversations !== null ? kpis.openConversations.toString() : '—',
        icon: 'pi pi-inbox',
        trendText: '',
        trendGood: null,
        sub: kpis.openConversations !== null ? 'in corso ora' : 'dato di team',
      },
      {
        label: 'Da Assegnare',
        value: kpis.unassignedConversations !== null ? kpis.unassignedConversations.toString() : '—',
        icon: 'pi pi-bell',
        trendText: '',
        trendGood: null,
        sub: kpis.unassignedConversations !== null ? 'richiedono attenzione' : 'dato di team',
      },
      {
        label: 'Tempo Medio Risposta',
        value: kpis.avgResponseTimeMin + ' min',
        icon: 'pi pi-clock',
        trendText: respDiff !== 0 ? (respDiff > 0 ? '+' : '') + respDiff + ' min' : '',
        trendGood: respDiff !== 0 ? respDiff <= 0 : null,
        sub: 'vs settimana precedente',
      },
      {
        label: 'SLA Rispettati',
        value: kpis.slaPercent + '%',
        icon: 'pi pi-check-circle',
        trendText: slaTrend?.text ?? '',
        trendGood: slaTrend?.good ?? null,
        sub: 'obiettivo: 90%',
      },
      {
        label: 'Operatori Attivi',
        value: kpis.activeOperatorsCount + ' / ' + kpis.totalOperatorsCount,
        icon: 'pi pi-users',
        trendText: '',
        trendGood: null,
        sub: isFiltered ? 'nella selezione' : '',
      },
    ];
  }

  private pctTrend(curr: number, prev: number): { text: string; good: boolean } | null {
    if (!prev) return null;
    const pct = Math.round(((curr - prev) / prev) * 100);
    return { text: (pct >= 0 ? '+' : '') + pct + '%', good: pct >= 0 };
  }

  // ─── Charts ───────────────────────────────────────────────────────────────

  private buildTrendChart(data: DashboardResponse) {
    const { trend, period } = data;
    this.trendData = {
      labels: trend.labels,
      datasets: [
        {
          label: period.label,
          data: trend.current,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16,185,129,0.08)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#10b981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
        },
        ...(period.prevLabel ? [{
          label: period.prevLabel,
          data: trend.previous,
          borderColor: '#94a3b8',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          tension: 0.4,
          pointBackgroundColor: '#94a3b8',
          pointRadius: 3,
        }] : []),
      ],
    };
  }

  private buildStatusChart(data: DashboardResponse) {
    const { statusDistribution: s } = data;
    this.statusDoughnutData = {
      labels: ['Da assegnare', 'Pendenti', 'Aperte', 'Chiuse'],
      datasets: [{
        data: [s.unassigned, s.pending, s.open, s.closed],
        backgroundColor: [
          'rgba(59,130,246,0.6)',
          'rgba(245,158,11,0.6)',
          'rgba(16,185,129,0.6)',
          'rgba(239,68,68,0.6)',
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 6,
      }],
    };
  }

  private buildOperatorCharts(data: DashboardResponse) {
    const ops = data.operators;
    const labels = ops.map(op => `${op.firstName} ${op.lastName[0]}.`);

    this.operatorBarData = {
      labels,
      datasets: [{
        data: ops.map(op => op.conversationsCount),
        backgroundColor: BAR_COLORS.slice(0, ops.length),
        borderRadius: 4,
        borderWidth: 0,
      }],
    };

    this.responseTimeData = {
      labels,
      datasets: [{
        data: ops.map(op => op.avgResponseTimeMin),
        backgroundColor: BAR_COLORS.slice(0, ops.length),
        borderRadius: 4,
        borderWidth: 0,
      }],
    };
  }

  private initChartOptions() {
    const textColor = 'rgba(107,114,128,0.9)';
    const gridColor = 'rgba(107,114,128,0.08)';

    this.lineChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top', labels: { color: textColor, usePointStyle: true, boxWidth: 8, padding: 20, font: { size: 12 } } },
      },
      scales: {
        x: { ticks: { color: textColor, font: { size: 12 } }, grid: { color: gridColor } },
        y: { ticks: { color: textColor, font: { size: 12 } }, grid: { color: gridColor }, beginAtZero: true },
      },
    };

    this.doughnutChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '68%',
      plugins: {
        legend: { position: 'bottom', labels: { color: textColor, usePointStyle: true, boxWidth: 8, padding: 16, font: { size: 12 } } },
      },
    };

    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: { legend: { display: false } },
      scales: {
        x: { ticks: { color: textColor, font: { size: 12 } }, grid: { color: gridColor }, beginAtZero: true },
        y: { ticks: { color: textColor, font: { size: 12 } }, grid: { color: 'transparent' } },
      },
    };
  }

  // ─── Date helpers ─────────────────────────────────────────────────────────

  private getWeekDates(): { dateFrom: string; dateTo: string } {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysToMonday + this.weekOffset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { dateFrom: this.toIsoDate(monday), dateTo: this.toIsoDate(sunday) };
  }

  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private formatWeekRange(dateFrom: string, dateTo: string): string {
    const months = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
    const from = new Date(dateFrom + 'T00:00:00');
    const to = new Date(dateTo + 'T00:00:00');
    return `${from.getDate()} – ${to.getDate()} ${months[to.getMonth()]}`;
  }

  // ─── Status / priority helpers ────────────────────────────────────────────

  getStatusKey(status: string): string {
    const map: Record<string, string> = {
      'TO_ASSIGN': 'unassigned',
      'ASSIGNED_PENDING': 'pending',
      'OPEN': 'open',
      'CLOSED': 'closed',
    };
    return map[status] ?? 'open';
  }

  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      'TO_ASSIGN': 'Da assegnare',
      'ASSIGNED_PENDING': 'Pendente',
      'OPEN': 'Aperta',
      'CLOSED': 'Chiusa',
    };
    return map[status] ?? status;
  }

  getStatusIcon(status: string): string {
    const map: Record<string, string> = {
      'OPEN': 'fa-light fa-circle-play',
      'CLOSED': 'fa-light fa-ban',
      'ASSIGNED_PENDING': 'fa-light fa-circle-pause',
      'TO_ASSIGN': 'fa-light fa-circle-plus',
    };
    return map[status] ?? 'fa-light fa-circle';
  }

  getPriorityKey(priority: string): string {
    const map: Record<string, string> = {
      'HIGH': 'high',
      'MEDIUM': 'medium',
      'LOW': 'low',
      'DEFAULT': 'none',
    };
    return map[priority] ?? 'none';
  }

  getPriorityLabel(priority: string): string {
    const map: Record<string, string> = {
      'HIGH': 'Alta',
      'MEDIUM': 'Normale',
      'LOW': 'Bassa',
      'DEFAULT': '—',
    };
    return map[priority] ?? '—';
  }

  getPriorityIcon(priority: string): string {
    const map: Record<string, string> = {
      'HIGH': 'fa-duotone fa-solid fa-signal-bars',
      'MEDIUM': 'fa-duotone fa-solid fa-signal-bars-fair',
      'LOW': 'fa-duotone fa-solid fa-signal-bars-weak',
      'DEFAULT': 'fa-duotone fa-solid fa-signal-bars-slash',
    };
    return map[priority] ?? 'fa-duotone fa-solid fa-signal-bars-slash';
  }
}
